<script setup>
  /* global defineProps */
  import { computed, ref, toRef, watch } from 'vue'
  import { ElMessage } from 'element-plus'
  import { replayLanguageKey, useReplayRangeAnalysis } from '../../composables/useReplayRangeAnalysis'
  import ReplayPlayer from './ReplayPlayer.vue'
  import ReplayOutputAttempts from './ReplayOutputAttempts.vue'
  import ReplayPolicyPanel from './ReplayPolicyPanel.vue'
  import ReplayOutputCards from './ReplayOutputCards.vue'
  import { formatGenerationDuration, formatReplayLanguage } from './replayLabels'
  import ReplayRangeEditor from './ReplayRangeEditor.vue'
  import ReplayPreflightDialog from './ReplayPreflightDialog.vue'
  import {
    bestReplayVersion,
    initialReplayRequest,
    playable,
    useReplayGeneration,
  } from '../../composables/useReplayGeneration'
  const props = defineProps({
    meetingCode: { type: String, required: true },
  })
  const {
    inventory,
    versions,
    preflight,
    loading,
    busy,
    error,
    active,
    generationBusy,
    refresh,
    inspect,
    confirm,
    retry,
    regenerate,
    discard,
  } = useReplayGeneration(toRef(props, 'meetingCode'))
  const editing = ref(false),
    selection = ref(''),
    request = ref(null)
  watch(
    () => props.meetingCode,
    () => {
      request.value = null
      editing.value = false
      selection.value = ''
    }
  )
  watch(inventory, (assets) => {
    if (!assets || request.value) return
    request.value = initialReplayRequest(assets, versions.value)
  })
  const {
    analysis,
    loading: analyzing,
    error: analysisError,
    refresh: analyze,
  } = useReplayRangeAnalysis(toRef(props, 'meetingCode'), request, editing)
  const removedLanguages = ref('')
  watch(analysis, (value) => {
    removedLanguages.value = ''
    if (!value || !request.value) return
    const available = new Map(
      (value.languageOptions || []).map((option) => [replayLanguageKey(option.language), option.language])
    )
    const selected = request.value.languages || []
    const removed = selected.filter((language) => !available.has(replayLanguageKey(language)))
    if (removed.length)
      removedLanguages.value = `当前范围不含${removed
        .map((language) => formatReplayLanguage(language))
        .join('、')}字幕，已取消对应配音`
    request.value = {
      ...request.value,
      languages: [...new Set(selected.map((language) => available.get(replayLanguageKey(language))).filter(Boolean))],
    }
  })
  watch(request, () => discard(), { deep: true })
  const legacyVersions = computed(() => versions.value.filter((v) => v.generationId.startsWith('legacy:')))
  const legacyTask = computed(() => legacyVersions.value[0] || null)
  const hasLegacy = computed(() => playable(legacyTask.value))
  const chosen = computed(() => {
    if (selection.value === 'legacy') return legacyTask.value
    if (selection.value.startsWith('legacy:'))
      return legacyVersions.value.find((v) => v.generationId === selection.value) || legacyTask.value
    if (selection.value) return versions.value.find((v) => v.generationId === selection.value) || null
    const best = bestReplayVersion(versions.value)
    if (hasLegacy.value && (!best || !best.outputs.some((o) => o.id !== 'baseline' && o.videoUrl)))
      return legacyTask.value
    return best || legacyTask.value
  })
  const playerTask = computed(() =>
    chosen.value ? { ...chosen.value, taskId: chosen.value.generationId || chosen.value.taskId } : null
  )
  const latest = computed(() => versions.value[0])
  const production = computed(() => active.value || (latest.value?.status === 'BLOCKED' ? latest.value : null))
  const newAssets = computed(
    () => inventory.value && latest.value && inventory.value.assetRevision > latest.value.assetRevision
  )
  const afterEnd = computed(
    () => inventory.value?.confirmedEndedAtMs && inventory.value.assetRevision > inventory.value.confirmedAssetRevision
  )
  const valid = computed(
    () => request.value?.ranges?.length && request.value.ranges.every((r) => r.startAtMs > 0 && r.endAtMs > r.startAtMs)
  )
  const status = (version) =>
    version?.workActive && !['QUEUED', 'PROCESSING', 'RETRY_WAIT'].includes(version.status)
      ? '语种制作中'
      : {
          QUEUED: '排队中',
          PROCESSING: '制作中',
          RETRY_WAIT: '等待自动继续',
          BLOCKED: '处理受阻',
          POSTPONED: '会议延期，等待重新排期',
          COMPLETED: '已完成',
          COMPLETED_WITH_WARNINGS: '可播放，含补齐内容',
        }[version?.status] || '未生成'
  const phase = (version) => {
    if (version?.workActive && !['QUEUED', 'PROCESSING', 'RETRY_WAIT'].includes(version.status)) return '保留已有成片'
    if (version?.generationId?.startsWith('legacy:'))
      return { QUEUED: '等待制作', PROCESSING: '视频制作中', BLOCKED: '需要继续处理' }[version.status] || '制作完成'
    return version?.phase?.startsWith('TTS:')
      ? `制作${formatReplayLanguage(version.phase.slice(4))}配音`
      : {
          BASELINE: '保存会议资料基础版',
          ORIGINAL: '合成原声与 PPT',
          QUEUED: '等待队列',
          RETRY_WAIT: '保留已有成片，稍后继续',
          COMPLETED: '制作完成',
          BLOCKED: '需要继续处理',
          POSTPONED: '按最新会议结束时间安排',
        }[version?.phase] || '准备素材'
  }
  const title = (version) =>
    version?.generationId?.startsWith('legacy:')
      ? '历史采集成片'
      : `${version?.generationIntent === 'FULL' ? '整场回顾' : '阶段回顾'}${
          version?.triggerSource === 'AUTO_SCHEDULED' ? ' · 自动生成' : ''
        }`
  const automatic = computed(
    () =>
      ({
        DISABLED: '自动生成未开启',
        CHECK_FAILED: '自动检查暂未完成，将继续重试',
        INVALID_END_TIME: '请补充有效会议结束时间',
        WAITING_END: `预计 ${time(inventory.value?.automaticDueAtMs)} 开始生成`,
        WAITING_SCHEDULE: '等待核对会议日程',
        CAPTURING: '会议仍在采集，生成时间顺延',
        WAITING_MATERIALS: '等待素材落库',
        WAITING_GENERATION: '当前任务完成后检查新增内容',
        SUBMITTED: '自动生成已提交',
        COVERED: '当前内容已有制作记录',
      }[inventory.value?.automaticStatus] || '')
  )
  const time = (ms) => (ms ? new Date(ms).toLocaleString() : '—')
  const submit = async () => {
    const result = await confirm()
    if (result) {
      editing.value = false
      ElMessage.success('已加入制作队列，现有成片可继续播放')
    }
  }
</script>
<template>
  <section v-loading="loading" class="generation-panel">
    <div v-if="error && !preflight" class="notice error" role="alert">
      {{ error }}
      <el-button size="small" @click="refresh()">重新加载</el-button>
    </div>
    <div v-if="afterEnd" class="notice">确认结束后仍有新增素材，当前回顾未包含；可选择内容生成新版。</div>
    <div v-else-if="newAssets" class="notice">有新增素材，现有回顾保持不变，可重新选择内容生成新版。</div>
    <div class="toolbar">
      <div>
        <h2>会议回顾</h2>
        <p>
          {{ inventory?.captures?.length || 0 }} 段已保存采集 ·
          {{ inventory?.confirmedEndedAtMs ? '已确认过会议结束' : '尚未确认整场结束' }}
        </p>
      </div>
      <el-button :disabled="!inventory || busy || Boolean(generationBusy)" type="primary" @click="editing = !editing">
        {{ generationBusy ? '正在生成' : editing ? '收起范围选择' : chosen ? '重新生成' : '生成回顾视频' }}
      </el-button>
    </div>
    <ReplayPolicyPanel :meeting-code="meetingCode" @changed="refresh()" />
    <p v-if="automatic" aria-live="polite">{{ automatic }}</p>
    <div v-if="production" aria-live="polite" class="production">
      <strong>{{ status(production) }} · {{ phase(production) }}</strong>
      <p>创建于 {{ time(production.createdAtMs) }} · 最近更新 {{ time(production.updatedAtMs) }}</p>
      <p>累计制作耗时 {{ formatGenerationDuration(production.generationDurationMs) }}</p>
      <el-progress
        v-if="production.status === 'PROCESSING'"
        :percentage="Math.min(99, Math.max(0, production.progress || 0))"
      />
      <ReplayOutputCards
        v-if="production.generationId !== chosen?.generationId"
        :outputs="production.outputs"
        readonly
        :task-status="production.status"
      />
      <p v-if="playable(production)">基础成片已保存，可在版本列表中播放。</p>
    </div>
    <div v-if="editing && inventory && request" class="editor">
      <p v-if="versions.length">已沿用上次时间范围，新增内容可调整时间段后纳入。</p>
      <ReplayRangeEditor
        v-model="request"
        :analysis="analysis"
        :analysis-error="analysisError"
        :analyzing="analyzing"
        :disabled="busy"
        :inventory="inventory"
        @reanalyze="analyze"
      />
      <p v-if="removedLanguages" role="status">{{ removedLanguages }}</p>
      <div class="actions">
        <span v-if="!valid">请选择有效的开始和结束时间</span>
        <el-button
          :disabled="!valid || analyzing || !analysis || analysis.sourceConflict || Boolean(generationBusy)"
          :loading="busy"
          type="primary"
          @click="inspect(request)"
        >
          预检生成内容
        </el-button>
      </div>
    </div>
    <div class="playback">
      <div v-if="versions.length || hasLegacy" class="version-picker">
        <label>
          播放版本
          <select v-model="selection">
            <option value="">当前最佳可用成片</option>
            <option
              v-for="version in versions.filter(playable)"
              :key="version.generationId"
              :value="version.generationId"
            >
              {{ time(version.createdAtMs) }} · {{ title(version) }} · {{ status(version) }}
            </option>
          </select>
        </label>
      </div>
      <p v-if="chosen">累计制作耗时 {{ formatGenerationDuration(chosen.generationDurationMs) }}</p>
      <ReplayPlayer
        :allow-regenerate="Boolean(chosen && !chosen.generationId.startsWith('legacy:'))"
        :busy="busy"
        :task="playerTask"
        @regenerate="regenerate(chosen.generationId, $event.id)"
      />
    </div>
    <details v-if="versions.length" class="history">
      <summary>生成记录（{{ versions.length }}）</summary>
      <div v-for="version in versions" :key="version.generationId" class="version-row">
        <div>
          <strong>{{ time(version.createdAtMs) }} · {{ title(version) }}</strong>
          <p>{{ status(version) }} · {{ phase(version) }}</p>
          <p>累计制作耗时 {{ formatGenerationDuration(version.generationDurationMs) }}</p>
          <ReplayOutputAttempts :attempts="version.outputAttempts || []" />
          <p v-if="version.errorMessage" class="error">{{ version.errorMessage }}</p>
        </div>
        <el-button
          v-if="version.retryable"
          :disabled="busy || Boolean(generationBusy)"
          size="small"
          @click="retry(version.generationId)"
        >
          继续处理
        </el-button>
      </div>
    </details>
    <ReplayPreflightDialog
      v-if="preflight"
      :busy="busy"
      :error="error"
      :generation-busy="Boolean(generationBusy)"
      :plan="preflight"
      @back="discard"
      @confirm="submit"
    />
  </section>
</template>
<style scoped>
  .generation-panel {
    margin-top: 20px;
    padding: 24px;
    background: #fff;
    border: 1px solid #e5eaf0;
    border-radius: 8px;
  }
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  h2 {
    margin: 0;
    font-size: 22px;
  }
  p {
    color: #6b7686;
    font-size: 13px;
    line-height: 1.6;
  }
  .notice {
    padding: 14px;
    margin-bottom: 16px;
    background: #fff8e8;
    border-radius: 6px;
    color: #78521a;
    line-height: 1.6;
  }
  .error {
    color: #b73838;
    overflow-wrap: anywhere;
  }
  .production {
    padding: 18px;
    margin: 20px 0;
    background: #f2f7ff;
    border-radius: 6px;
  }
  .editor {
    margin: 24px 0;
  }
  .actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 16px;
    gap: 14px;
  }
  .actions span {
    font-size: 13px;
    color: #8a641f;
  }
  .playback {
    margin-top: 24px;
  }
  .version-picker {
    margin-bottom: 16px;
  }
  select {
    max-width: 100%;
    padding: 8px;
    border: 1px solid #cbd3df;
    border-radius: 4px;
    background: #fff;
  }
  .history {
    margin-top: 24px;
  }
  summary {
    cursor: pointer;
    padding: 10px 0;
  }
  .version-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
    border-top: 1px solid #e5eaf0;
    gap: 12px;
  }
  .version-row p {
    margin: 6px 0;
  }
  @media (max-width: 680px) {
    .generation-panel {
      padding: 16px;
    }
    .toolbar {
      align-items: start;
      flex-direction: column;
    }
  }
</style>
